// The video editor's shell strings (components/Editor), under t('editor.rve.shell.<key>').
// Same keys in en, es, pt and ar; Arabic follows ../../ARABIC_STYLE.md.
export default {
  topBar: {
    unsavedChanges: 'Unsaved changes',
    savesToAccountHint: 'Changes save to your account as you work',
    savesAutomatically: 'Saves automatically',
    justNow: 'just now',
    pendingHint: "Your account couldn't be reached, so this is saved in this browser; it keeps trying.",
    deviceHint: "Your account couldn't be reached when this opened, so changes stay in this browser. Reopen the page to save them to your account.",
    savedOnDevice: 'Saved on this device {when}',
    savedToAccount: 'Saved to your account',
    saved: 'Saved',
    savedWhen: 'Saved {when}',
    backToProject: 'Back to project',
    projectName: 'Project name',
    rename: 'Rename',
    undo: 'Undo',
    redo: 'Redo',
    videoFormat: 'Video format',
    format: 'Format',
    keyboardShortcuts: 'Keyboard shortcuts',
    watermarkHint: 'Exports on the free plan carry a small watermark',
    removeWatermark: 'Remove watermark',
    exporting: 'Exporting {progress}%',
    export: 'Export',
  },
  formats: {
    vertical: { label: 'Vertical', hint: 'TikTok, Reels, Shorts' },
    square: { label: 'Square', hint: 'Feed posts' },
    portrait: { label: 'Portrait', hint: 'Instagram, Facebook' },
    landscape: { label: 'Landscape', hint: 'YouTube, web' },
  },
  exportDialog: {
    title: 'Export video',
    description: 'An MP4 made from everything on the timeline.',
    format: 'Format',
    size: 'Size',
    length: 'Length',
    ready: 'Your video is ready',
    download: 'Download',
    exportAgain: 'Export again',
    starting: 'Starting…',
    rendering: 'Rendering',
    keepEditing: 'You can close this and keep editing: the export carries on, and changes you make now aren’t in it.',
    failed: 'The export failed.',
    tryAgain: 'Try again',
    watermark: 'Free plan exports carry a small watermark.',
    upgrade: 'Upgrade',
    tooLong: 'Exports can be up to 15 minutes long, and this video is {length}. Cut it down on the timeline or in the transcript, then export.',
    addSomethingFirst: 'Add something to the timeline first',
    exportVideo: 'Export video',
    earlierExports: 'Earlier exports',
  },
  shortcuts: {
    title: 'Keyboard shortcuts',
    description: 'They work anywhere in the editor except while you type in a field.',
    playPause: 'Play / pause',
    frameStep: 'Back / forward one frame',
    secondStep: 'Back / forward one second',
    jumpEnds: 'Jump to start / end',
    split: 'Split the selected layer at the playhead',
    delete: 'Delete the selected layer',
    duplicate: 'Duplicate the selected layer',
    undo: 'Undo',
    redo: 'Redo',
    saveNow: 'Save now',
    export: 'Export',
    deselect: 'Deselect',
    showShortcuts: 'Show shortcuts',
  },
  inspector: {
    kinds: {
      text: 'Text',
      video: 'Video',
      image: 'Image',
      audio: 'Audio',
      captions: 'Captions',
      sticker: 'Sticker',
      progressBar: 'Progress bar',
    },
    noSettings: 'This layer has no settings.',
    format: 'Format',
    background: 'Background',
    backgroundColor: 'Background {color}',
    customColour: 'Custom colour',
    customBackground: 'Custom background colour',
    backgroundHint: 'Shows wherever no layer covers the canvas.',
    safeZones: 'Safe zones',
    safeZonesHint: 'Shade where TikTok, Reels and Shorts put their buttons and captions, so nothing important sits under them.',
    showSafeZones: 'Show safe zones',
    summaryOne: '1 layer · {length} long. Select a layer on the canvas or the timeline to change it.',
    summaryMany: '{n} layers · {length} long. Select a layer on the canvas or the timeline to change it.',
    duplicateLayer: 'Duplicate layer',
    duplicate: 'Duplicate',
    deleteLayer: 'Delete layer',
    delete: 'Delete',
    thisVideo: 'This video',
    hideProperties: 'Hide properties',
    properties: 'Properties',
  },
  library: {
    nav: 'Library',
    hidePanel: 'Hide panel',
    transcript: { label: 'Transcript', title: 'Transcript' },
    captions: { label: 'Captions', title: 'Captions' },
    text: { label: 'Text', title: 'Text' },
    uploads: { label: 'Uploads', title: 'Your uploads' },
    stock: { label: 'Stock', title: 'Stock videos' },
    images: { label: 'Images', title: 'Images' },
    audio: { label: 'Audio', title: 'Music & sound' },
    brand: { label: 'Brand', title: 'Brand kit' },
    exports: { label: 'Exports', title: 'Exports' },
  },
  mobile: {
    nav: 'Edit',
    transcript: 'Transcript',
    captions: 'Captions',
    text: 'Text',
    format: 'Format',
    exports: 'Exports',
  },
  brand: {
    logoUpdated: 'Logo updated',
    logoAdded: 'Logo added',
    captionStyleSaved: 'Caption style saved',
    captionsRestyled: 'Captions restyled',
    logo: 'Logo',
    upload: 'Upload',
    pngHint: "PNG, JPEG or WebP up to 2 MB. A PNG with a transparent background looks best.",
    corner: 'Corner',
    corners: {
      topLeft: 'top left',
      topRight: 'top right',
      bottomLeft: 'bottom left',
      bottomRight: 'bottom right',
    },
    size: 'Size',
    opacity: 'Opacity',
    updateLogo: 'Update the logo on this video',
    addLogo: 'Add the logo to this video',
    captionStyle: 'Caption style',
    savedStyle: 'Saved: {font} captions.',
    customFont: 'custom',
    styleHint: 'Style one video’s captions the way you like, save them here, and give every video the same look.',
    saveCurrent: 'Save current',
    applySaved: 'Apply saved',
    accountHint: "Your brand kit belongs to your account: the same logo and caption style here, in the clip editor and on every clip you export.",
    replace: "Replace",
    remove: "Remove the logo",
    failed: "Couldn't save. Try again.",
    tooBig: "That picture is over 2 MB.",
    notPicture: "That isn't a PNG, JPEG or WebP picture.",
  },
  editor: {
    restored: 'Picked up where you left off: your edits from {ago}.',
    startOver: 'Start over from the original',
    dismiss: 'Dismiss',
    opening: 'Opening your project…',
    properties: 'Properties',
  },
  stage: {
    frameError: 'This frame couldn’t be drawn',
    layerFailed: 'A layer failed to load.',
    linkExpired: 'A media link may have expired: reopening the project fetches fresh ones.',
    startWithVideo: 'Start with a video',
    startHint: 'Bring in footage, then trim it, caption it and add text on the timeline below.',
    uploadVideo: 'Upload a video',
    browseStock: 'Browse stock footage',
    addTitle: 'Add a title',
    startHintPhone: 'Bring in footage, then trim it, caption it and add text from the tabs below.',
  },
  timelineLabels: {
    text: 'Text',
    image: 'Image',
    video: 'Video',
    audio: 'Audio',
    caption: 'Caption',
    sticker: 'Sticker',
    progressBar: 'Progress bar',
    shape: 'Shape',
    item: 'Item',
  },
  renders: {
    countOne: '1 export',
    countMany: '{n} exports',
    none: 'No exports yet',
    refresh: 'Refresh',
    nothingYet: 'Nothing exported yet',
    keptHere: 'Exports you make from the editor are kept here, ready to download again.',
    exportThis: 'Export this video',
    exporting: 'Exporting',
    video: 'Video',
    failed: 'Export failed',
    download: 'Download',
  },
  page: {
    opening: 'Opening your project…',
    openFailed: 'This project couldn’t be opened.',
    clipFailed: 'This clip couldn’t be opened',
    projectFailed: 'This project couldn’t be opened',
    goBack: 'Go back',
    seoTitle: 'Video Editor - Professional Online Video Editing Tool',
    seoDescription:
      'Edit your videos with our powerful online video editor. Add captions, overlays, animations, and effects. Timeline-based editing with real-time preview. Perfect for creating engaging social media content.',
  },
};
